import React, { useState, useEffect, useCallback, useRef, FormEvent } from 'react';
import { formatPhoneInput } from '../utils/formatPhone';
import { parseServerDate } from '../utils/dateUtils';
import { apiGet, apiPatch, apiPost, apiPut, apiDelete } from '../api/client';
import type {
  FuneralPlanListResponse,
  FuneralPlanSummary,
  FuneralPlanResponse,
  CreateFuneralPlanRequest,
  UpdateFuneralPlanRequest,
} from '../types/api';
import { VaultRole } from '../types/api';
import AlertMessage from './AlertMessage';
import LoadingSpinner from './LoadingSpinner';
import Skeleton from './Skeleton';
import EmptyState from './EmptyState';
import SortableList from './SortableList';
import './FuneralPlansList.css';

interface Props {
  vaultId: string;
  userRole: VaultRole;
  onCountChange?: (count: number) => void;
}

const SERVICE_TYPES = [
  '', 'Funeral Service', 'Memorial', 'Celebration of Life',
  'Home Going', 'Graveside Only', 'Let Family Decide',
];

const DISPOSITION_TYPES = [
  '', 'Traditional Burial', 'Cremation', 'Green Burial',
  'Donation to Science', 'Undecided',
];

const CASKET_OPTIONS = ['', 'Open', 'Closed', 'No Preference'];

const VIEWING_OPTIONS = [
  '', 'Public', 'Private', 'No Viewing', 'Let Family Decide',
];

const FLOWER_OPTIONS = [
  '', 'Flowers Welcome', 'In Lieu of Flowers', 'Both', 'No Preference',
];

const ORGAN_OPTIONS = [
  '', 'Yes', 'No', 'Already Registered', 'No Preference',
];

type FormData = Omit<CreateFuneralPlanRequest, 'personName'> & { personName: string };

function emptyForm(): FormData {
  return {
    personName: '',
    overallWishes: null,
    serviceType: null,
    serviceLocation: null,
    officiant: null,
    musicSelections: null,
    scriptureReadings: null,
    readings: null,
    eulogyBy: null,
    pallbearers: null,
    specialTributes: null,
    dispositionType: null,
    cemeteryName: null,
    cemeteryLocation: null,
    plotInformation: null,
    casketPreference: null,
    vaultPreference: null,
    openClosedCasket: null,
    urnPreference: null,
    remainsDisposition: null,
    viewingWishes: null,
    viewingNotes: null,
    clothingPreference: null,
    personalItems: null,
    flowerPreference: null,
    donationOrganizations: null,
    obituaryWishes: null,
    receptionWishes: null,
    funeralHome: null,
    funeralHomePhone: null,
    prePaidArrangements: null,
    veteranInfo: null,
    organDonation: null,
    additionalWishes: null,
  };
}

function responseToForm(p: FuneralPlanResponse): FormData {
  return {
    personName: p.personName,
    overallWishes: p.overallWishes,
    serviceType: p.serviceType,
    serviceLocation: p.serviceLocation,
    officiant: p.officiant,
    musicSelections: p.musicSelections,
    scriptureReadings: p.scriptureReadings,
    readings: p.readings,
    eulogyBy: p.eulogyBy,
    pallbearers: p.pallbearers,
    specialTributes: p.specialTributes,
    dispositionType: p.dispositionType,
    cemeteryName: p.cemeteryName,
    cemeteryLocation: p.cemeteryLocation,
    plotInformation: p.plotInformation,
    casketPreference: p.casketPreference,
    vaultPreference: p.vaultPreference,
    openClosedCasket: p.openClosedCasket,
    urnPreference: p.urnPreference,
    remainsDisposition: p.remainsDisposition,
    viewingWishes: p.viewingWishes,
    viewingNotes: p.viewingNotes,
    clothingPreference: p.clothingPreference,
    personalItems: p.personalItems,
    flowerPreference: p.flowerPreference,
    donationOrganizations: p.donationOrganizations,
    obituaryWishes: p.obituaryWishes,
    receptionWishes: p.receptionWishes,
    funeralHome: p.funeralHome,
    funeralHomePhone: p.funeralHomePhone,
    prePaidArrangements: p.prePaidArrangements,
    veteranInfo: p.veteranInfo,
    organDonation: p.organDonation,
    additionalWishes: p.additionalWishes,
  };
}

function formToRequest(f: FormData): CreateFuneralPlanRequest {
  const req: any = { personName: f.personName };
  const keys = Object.keys(f) as (keyof FormData)[];
  for (const k of keys) {
    if (k === 'personName') continue;
    const v = f[k];
    req[k] = (typeof v === 'string' && v.trim() === '') ? null : v;
  }
  return req;
}

export default function FuneralPlansList({ vaultId, userRole, onCountChange }: Props) {
  const onCountChangeRef = useRef(onCountChange);
  onCountChangeRef.current = onCountChange;

  const [plans, setPlans] = useState<FuneralPlanSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const canEdit = userRole === VaultRole.Owner || userRole === VaultRole.Spouse;

  // Detail / form state
  const [viewPlan, setViewPlan] = useState<FuneralPlanResponse | null>(null);
  const [editing, setEditing] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState<FormData>(emptyForm());
  const [saving, setSaving] = useState(false);

  // Delete state
  const [confirmDelete, setConfirmDelete] = useState<FuneralPlanSummary | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Print from list view
  const [printPlan, setPrintPlan] = useState<FuneralPlanResponse | null>(null);

  const loadPlans = useCallback(async () => {
    try {
      const data = await apiGet<FuneralPlanListResponse>(`/api/vault/${vaultId}/funeral-plans`);
      setPlans(data.plans);
      onCountChangeRef.current?.(data.plans.length);
    } catch (err: any) {
      setError(err.message || 'Failed to load funeral plans.');
    } finally {
      setLoading(false);
    }
  }, [vaultId]);

  useEffect(() => { loadPlans(); }, [loadPlans]);

  async function handleReorder(reordered: FuneralPlanSummary[]) {
    setPlans(reordered);
    try {
      await apiPatch(`/api/vault/${vaultId}/funeral-plans/reorder`, {
        orderedIds: reordered.map(p => p.id),
      });
    } catch {
      await loadPlans();
    }
  }

  function setField(field: keyof FormData, value: string) {
    setForm(prev => ({ ...prev, [field]: value }));
  }

  async function handleOpenPlan(planId: string) {
    setError('');
    try {
      const plan = await apiGet<FuneralPlanResponse>(`/api/vault/${vaultId}/funeral-plans/${planId}`);
      setViewPlan(plan);
      setEditing(false);
      setCreating(false);
    } catch (err: any) {
      setError(err.message || 'Failed to load plan.');
    }
  }

  function handleStartCreate() {
    setForm(emptyForm());
    setCreating(true);
    setEditing(false);
    setViewPlan(null);
  }

  function handleStartEdit() {
    if (!viewPlan) return;
    setForm(responseToForm(viewPlan));
    setEditing(true);
  }

  function handleCancelForm() {
    setCreating(false);
    setEditing(false);
  }

  async function handleSave(e: FormEvent) {
    e.preventDefault();
    setError('');
    setSaving(true);

    try {
      const body = formToRequest(form);
      if (creating) {
        await apiPost(`/api/vault/${vaultId}/funeral-plans`, body);
        setCreating(false);
      } else if (editing && viewPlan) {
        const updated = await apiPut<FuneralPlanResponse>(
          `/api/vault/${vaultId}/funeral-plans/${viewPlan.id}`, body as UpdateFuneralPlanRequest
        );
        setViewPlan(updated);
        setEditing(false);
      }
      await loadPlans();
    } catch (err: any) {
      setError(err.message || 'Failed to save plan.');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!confirmDelete) return;
    setDeleting(true);
    setError('');
    try {
      await apiDelete(`/api/vault/${vaultId}/funeral-plans/${confirmDelete.id}`);
      setConfirmDelete(null);
      if (viewPlan?.id === confirmDelete.id) {
        setViewPlan(null);
        setEditing(false);
      }
      await loadPlans();
    } catch (err: any) {
      setError(err.message || 'Delete failed.');
      setConfirmDelete(null);
    } finally {
      setDeleting(false);
    }
  }

  async function handlePrintFromList(e: React.MouseEvent, planId: string) {
    e.stopPropagation();
    try {
      const plan = await apiGet<FuneralPlanResponse>(`/api/vault/${vaultId}/funeral-plans/${planId}`);
      setPrintPlan(plan);
      // Wait for render, then print
      setTimeout(() => {
        window.print();
        setPrintPlan(null);
      }, 100);
    } catch (err: any) {
      setError(err.message || 'Failed to load plan for printing.');
    }
  }

  async function handleEditFromList(e: React.MouseEvent, planId: string) {
    e.stopPropagation();
    setError('');
    try {
      const plan = await apiGet<FuneralPlanResponse>(`/api/vault/${vaultId}/funeral-plans/${planId}`);
      setViewPlan(plan);
      setForm(responseToForm(plan));
      setEditing(true);
      setCreating(false);
    } catch (err: any) {
      setError(err.message || 'Failed to load plan.');
    }
  }

  function handleDeleteFromList(e: React.MouseEvent, plan: FuneralPlanSummary) {
    e.stopPropagation();
    setConfirmDelete(plan);
  }

  if (loading) return <Skeleton variant="card" count={2} />;

  // ─── Detail / read-only view ───
  if (viewPlan && !editing) {
    return (
      <div className="fp-detail">
        <button className="fp-back-btn" onClick={() => setViewPlan(null)}>&larr; All Plans</button>
        <div className="fp-detail-header">
          <h3>{viewPlan.personName}</h3>
          <div className="fp-detail-actions">
            <button className="fp-print-btn" onClick={() => window.print()}>Print</button>
            <button className="fp-edit-btn" onClick={handleStartEdit} disabled={!canEdit}>Edit</button>
            <button className="fp-delete-btn" onClick={() => {
              const summary: FuneralPlanSummary = {
                id: viewPlan.id,
                personName: viewPlan.personName,
                serviceType: viewPlan.serviceType,
                dispositionType: viewPlan.dispositionType,
                sortOrder: 0,
                updatedAt: viewPlan.updatedAt,
                createdAt: viewPlan.createdAt,
              };
              setConfirmDelete(summary);
            }} disabled={!canEdit}>Delete</button>
          </div>
        </div>

        <AlertMessage type="error" message={error} />

        <ReadOnlyView plan={viewPlan} />

        {confirmDelete && (
          <DeleteModal
            name={confirmDelete.personName}
            deleting={deleting}
            onConfirm={handleDelete}
            onCancel={() => setConfirmDelete(null)}
          />
        )}
      </div>
    );
  }

  // ─── Create / Edit form ───
  if (creating || editing) {
    return (
      <div className="fp-form-container">
        <button className="fp-back-btn" onClick={handleCancelForm}>
          &larr; {creating ? 'All Plans' : viewPlan?.personName || 'Back'}
        </button>
        <h3 className="fp-form-title">{creating ? 'New Funeral Plan' : `Edit: ${viewPlan?.personName}`}</h3>

        <AlertMessage type="error" message={error} />

        <form className="fp-form" onSubmit={handleSave}>
          <PlanFormFields form={form} setField={setField} />
          <div className="fp-form-actions">
            <button className="btn-secondary" type="button" onClick={handleCancelForm} disabled={saving}>
              Cancel
            </button>
            <button className="btn-primary" type="submit" disabled={saving}>
              {saving ? <LoadingSpinner small /> : (creating ? 'Create Plan' : 'Save Changes')}
            </button>
          </div>
        </form>
      </div>
    );
  }

  // ─── List view ───
  return (
    <div>
      <div className="fp-list-header">
        <h3>Funeral Plans</h3>
        <button className="btn-text" onClick={() => canEdit && handleStartCreate()} disabled={!canEdit}>
          + Add Plan
        </button>
      </div>

      <p className="fp-list-description">
        Record funeral service wishes and burial plans for each family member &mdash;
        service preferences, scripture readings, burial details, and more.
      </p>

      <AlertMessage type="error" message={error} />

      {plans.length === 0 ? (
        <EmptyState
          icon="funeral"
          title="No funeral plans yet"
          subtitle={canEdit ? "Click '+ Add Plan' above to create one." : undefined}
        />
      ) : (
        <div className="fp-cards">
          <SortableList
            items={plans}
            canReorder={canEdit}
            onReorder={handleReorder}
            renderItem={(p) => (
            <div className="fp-card" onClick={() => handleOpenPlan(p.id)}>
              <div className="fp-card-top">
                <div className="fp-card-name">{p.personName}</div>
                <div className="fp-card-actions">
                  <button
                    className="fp-card-action-btn"
                    onClick={(e) => handlePrintFromList(e, p.id)}
                    title="Print this plan"
                  >
                    Print
                  </button>
                  <button
                    className="fp-card-action-btn"
                    onClick={(e) => handleEditFromList(e, p.id)}
                    title="Edit this plan"
                    disabled={!canEdit}
                  >
                    Edit
                  </button>
                  <button
                    className="fp-card-action-btn fp-card-action-btn--danger"
                    onClick={(e) => handleDeleteFromList(e, p)}
                    title="Delete this plan"
                    disabled={!canEdit}
                  >
                    Delete
                  </button>
                </div>
              </div>
              <div className="fp-card-badges">
                {p.serviceType && <span className="fp-badge">{p.serviceType}</span>}
                {p.dispositionType && <span className="fp-badge fp-badge--secondary">{p.dispositionType}</span>}
              </div>
              <div className="fp-card-date">
                {p.updatedAt
                  ? `Updated ${parseServerDate(p.updatedAt)?.toLocaleDateString() ?? ''}`
                  : `Created ${parseServerDate(p.createdAt)?.toLocaleDateString() ?? ''}`}
              </div>
            </div>
            )}
          />
        </div>
      )}

      {confirmDelete && (
        <DeleteModal
          name={confirmDelete.personName}
          deleting={deleting}
          onConfirm={handleDelete}
          onCancel={() => setConfirmDelete(null)}
        />
      )}

      {printPlan && (
        <div className="fp-print-only">
          <div className="fp-detail">
            <div className="fp-detail-header">
              <h3>{printPlan.personName}</h3>
            </div>
            <ReadOnlyView plan={printPlan} />
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Read-only detail sections ───

function ReadOnlyView({ plan }: { plan: FuneralPlanResponse }) {
  return (
    <div className="fp-readonly-sections">
      <DetailSection title="About This Plan">
        <DetailRow label="Whose Plan" value={plan.personName} />
        <DetailRow label="Overall Wishes" value={plan.overallWishes} multiline />
      </DetailSection>

      <DetailSection title="Service">
        <DetailRow label="Service Type" value={plan.serviceType} />
        <DetailRow label="Location" value={plan.serviceLocation} />
        <DetailRow label="Officiant" value={plan.officiant} />
        <DetailRow label="Music Selections" value={plan.musicSelections} multiline />
        <DetailRow label="Scripture Readings" value={plan.scriptureReadings} multiline />
        <DetailRow label="Other Readings" value={plan.readings} multiline />
        <DetailRow label="Eulogy By" value={plan.eulogyBy} />
        <DetailRow label="Pallbearers" value={plan.pallbearers} multiline />
        <DetailRow label="Special Tributes" value={plan.specialTributes} multiline />
      </DetailSection>

      <DetailSection title="Burial & Disposition">
        <DetailRow label="Disposition Type" value={plan.dispositionType} />
        <DetailRow label="Cemetery" value={plan.cemeteryName} />
        <DetailRow label="Cemetery Location" value={plan.cemeteryLocation} />
        <DetailRow label="Plot Information" value={plan.plotInformation} />
        <DetailRow label="Casket Preference" value={plan.casketPreference} multiline />
        <DetailRow label="Burial Vault / Liner" value={plan.vaultPreference} />
        <DetailRow label="Open / Closed Casket" value={plan.openClosedCasket} />
        <DetailRow label="Urn Preference" value={plan.urnPreference} />
        <DetailRow label="Remains Disposition" value={plan.remainsDisposition} />
      </DetailSection>

      <DetailSection title="Visitation & Viewing">
        <DetailRow label="Viewing Wishes" value={plan.viewingWishes} />
        <DetailRow label="Notes" value={plan.viewingNotes} multiline />
      </DetailSection>

      <DetailSection title="Clothing & Appearance">
        <DetailRow label="Clothing Preference" value={plan.clothingPreference} multiline />
        <DetailRow label="Personal Items" value={plan.personalItems} multiline />
      </DetailSection>

      <DetailSection title="Flowers & Memorial Donations">
        <DetailRow label="Flower Preference" value={plan.flowerPreference} />
        <DetailRow label="Donation Organizations" value={plan.donationOrganizations} multiline />
      </DetailSection>

      <DetailSection title="Obituary">
        <DetailRow label="Obituary Wishes" value={plan.obituaryWishes} multiline />
      </DetailSection>

      <DetailSection title="Reception">
        <DetailRow label="Reception Wishes" value={plan.receptionWishes} multiline />
      </DetailSection>

      <DetailSection title="Funeral Home & Pre-Arrangements">
        <DetailRow label="Funeral Home" value={plan.funeralHome} />
        <DetailRow label="Phone" value={plan.funeralHomePhone} phone />
        <DetailRow label="Pre-Paid Arrangements" value={plan.prePaidArrangements} multiline />
      </DetailSection>

      <DetailSection title="Other">
        <DetailRow label="Veteran Information" value={plan.veteranInfo} multiline />
        <DetailRow label="Organ Donation" value={plan.organDonation} />
        <DetailRow label="Additional Wishes" value={plan.additionalWishes} multiline />
      </DetailSection>
    </div>
  );
}

function DetailSection({ title, children }: { title: string; children: React.ReactNode }) {
  const childArray = React.Children.toArray(children);
  const hasContent = childArray.some((child: any) => child?.props?.value);
  if (!hasContent) return null;

  return (
    <div className="fp-section">
      <h4 className="fp-section-title">{title}</h4>
      {children}
    </div>
  );
}

function DetailRow({ label, value, multiline, phone }: {
  label: string;
  value: string | null;
  multiline?: boolean;
  phone?: boolean;
}) {
  if (!value) return null;
  return (
    <div className="fp-detail-row">
      <div className="fp-detail-label">{label}</div>
      <div className={`fp-detail-value${multiline ? ' fp-detail-value--multiline' : ''}`}>
        {phone ? <a href={`tel:${value}`}>{value}</a> : value}
      </div>
    </div>
  );
}

// ─── Form fields ───

function PlanFormFields({ form, setField }: {
  form: FormData;
  setField: (field: keyof FormData, value: string) => void;
}) {
  return (
    <>
      <fieldset className="fp-fieldset">
        <legend className="fp-legend">About This Plan</legend>
        <div className="form-group">
          <label className="form-label" htmlFor="fp-personName">Whose plan is this? *</label>
          <input
            id="fp-personName"
            className="form-input"
            type="text"
            value={form.personName}
            onChange={e => setField('personName', e.target.value)}
            placeholder="e.g., James Barrett"
            required
            maxLength={200}
          />
        </div>
        <div className="form-group">
          <label className="form-label" htmlFor="fp-overallWishes">Overall Wishes</label>
          <textarea
            id="fp-overallWishes"
            className="form-input"
            value={form.overallWishes || ''}
            onChange={e => setField('overallWishes', e.target.value)}
            placeholder="General wishes, philosophy, or tone for your service..."
            maxLength={2000}
          />
        </div>
      </fieldset>

      <fieldset className="fp-fieldset">
        <legend className="fp-legend">Service</legend>
        <div className="form-group">
          <label className="form-label" htmlFor="fp-serviceType">Service Type</label>
          <select
            id="fp-serviceType"
            className="form-input"
            value={form.serviceType || ''}
            onChange={e => setField('serviceType', e.target.value)}
          >
            {SERVICE_TYPES.map(t => <option key={t} value={t}>{t || '-- Select --'}</option>)}
          </select>
        </div>
        <div className="form-group">
          <label className="form-label" htmlFor="fp-serviceLocation">Service Location</label>
          <input
            id="fp-serviceLocation"
            className="form-input"
            type="text"
            value={form.serviceLocation || ''}
            onChange={e => setField('serviceLocation', e.target.value)}
            placeholder="e.g., First Baptist Church, Memorial Funeral Home"
            maxLength={200}
          />
        </div>
        <div className="form-group">
          <label className="form-label" htmlFor="fp-officiant">Officiant</label>
          <input
            id="fp-officiant"
            className="form-input"
            type="text"
            value={form.officiant || ''}
            onChange={e => setField('officiant', e.target.value)}
            placeholder="e.g., Pastor John Smith, First Baptist Church"
            maxLength={200}
          />
        </div>
        <div className="form-group">
          <label className="form-label" htmlFor="fp-musicSelections">Music Selections</label>
          <textarea
            id="fp-musicSelections"
            className="form-input"
            value={form.musicSelections || ''}
            onChange={e => setField('musicSelections', e.target.value)}
            placeholder="e.g., Amazing Grace, How Great Thou Art, In the Garden"
            maxLength={2000}
          />
        </div>
        <div className="form-group">
          <label className="form-label" htmlFor="fp-scriptureReadings">Scripture Readings</label>
          <textarea
            id="fp-scriptureReadings"
            className="form-input"
            value={form.scriptureReadings || ''}
            onChange={e => setField('scriptureReadings', e.target.value)}
            placeholder="e.g., Psalm 23, John 14:1-6, Romans 8:38-39"
            maxLength={2000}
          />
        </div>
        <div className="form-group">
          <label className="form-label" htmlFor="fp-readings">Other Readings</label>
          <textarea
            id="fp-readings"
            className="form-input"
            value={form.readings || ''}
            onChange={e => setField('readings', e.target.value)}
            placeholder="Poems, letters, or other readings..."
            maxLength={2000}
          />
        </div>
        <div className="form-group">
          <label className="form-label" htmlFor="fp-eulogyBy">Eulogy By</label>
          <input
            id="fp-eulogyBy"
            className="form-input"
            type="text"
            value={form.eulogyBy || ''}
            onChange={e => setField('eulogyBy', e.target.value)}
            placeholder="Who should deliver the eulogy?"
            maxLength={500}
          />
        </div>
        <div className="form-group">
          <label className="form-label" htmlFor="fp-pallbearers">Pallbearers</label>
          <textarea
            id="fp-pallbearers"
            className="form-input"
            value={form.pallbearers || ''}
            onChange={e => setField('pallbearers', e.target.value)}
            placeholder="Names of requested pallbearers..."
            maxLength={2000}
          />
        </div>
        <div className="form-group">
          <label className="form-label" htmlFor="fp-specialTributes">Special Tributes</label>
          <textarea
            id="fp-specialTributes"
            className="form-input"
            value={form.specialTributes || ''}
            onChange={e => setField('specialTributes', e.target.value)}
            placeholder="Military honors, organization tributes, etc."
            maxLength={2000}
          />
        </div>
      </fieldset>

      <fieldset className="fp-fieldset">
        <legend className="fp-legend">Burial & Disposition</legend>
        <div className="form-group">
          <label className="form-label" htmlFor="fp-dispositionType">Disposition Type</label>
          <select
            id="fp-dispositionType"
            className="form-input"
            value={form.dispositionType || ''}
            onChange={e => setField('dispositionType', e.target.value)}
          >
            {DISPOSITION_TYPES.map(t => <option key={t} value={t}>{t || '-- Select --'}</option>)}
          </select>
        </div>
        <div className="form-group">
          <label className="form-label" htmlFor="fp-cemeteryName">Cemetery Name</label>
          <input
            id="fp-cemeteryName"
            className="form-input"
            type="text"
            value={form.cemeteryName || ''}
            onChange={e => setField('cemeteryName', e.target.value)}
            maxLength={200}
          />
        </div>
        <div className="form-group">
          <label className="form-label" htmlFor="fp-cemeteryLocation">Cemetery Location</label>
          <input
            id="fp-cemeteryLocation"
            className="form-input"
            type="text"
            value={form.cemeteryLocation || ''}
            onChange={e => setField('cemeteryLocation', e.target.value)}
            placeholder="Address, city, state"
            maxLength={500}
          />
        </div>
        <div className="form-group">
          <label className="form-label" htmlFor="fp-plotInformation">Plot Information</label>
          <input
            id="fp-plotInformation"
            className="form-input"
            type="text"
            value={form.plotInformation || ''}
            onChange={e => setField('plotInformation', e.target.value)}
            placeholder="Lot, section, pre-purchased?"
            maxLength={500}
          />
        </div>
        <div className="form-group">
          <label className="form-label" htmlFor="fp-casketPreference">Casket Preference</label>
          <textarea
            id="fp-casketPreference"
            className="form-input"
            value={form.casketPreference || ''}
            onChange={e => setField('casketPreference', e.target.value)}
            placeholder="Material, style, notes..."
            maxLength={1000}
          />
        </div>
        <div className="form-group">
          <label className="form-label" htmlFor="fp-vaultPreference">Burial Vault / Liner</label>
          <input
            id="fp-vaultPreference"
            className="form-input"
            type="text"
            value={form.vaultPreference || ''}
            onChange={e => setField('vaultPreference', e.target.value)}
            maxLength={500}
          />
        </div>
        <div className="form-group">
          <label className="form-label" htmlFor="fp-openClosedCasket">Open / Closed Casket</label>
          <select
            id="fp-openClosedCasket"
            className="form-input"
            value={form.openClosedCasket || ''}
            onChange={e => setField('openClosedCasket', e.target.value)}
          >
            {CASKET_OPTIONS.map(t => <option key={t} value={t}>{t || '-- Select --'}</option>)}
          </select>
        </div>
        <div className="form-group">
          <label className="form-label" htmlFor="fp-urnPreference">Urn Preference</label>
          <input
            id="fp-urnPreference"
            className="form-input"
            type="text"
            value={form.urnPreference || ''}
            onChange={e => setField('urnPreference', e.target.value)}
            placeholder="If cremation — style, material, etc."
            maxLength={500}
          />
        </div>
        <div className="form-group">
          <label className="form-label" htmlFor="fp-remainsDisposition">Remains Disposition</label>
          <input
            id="fp-remainsDisposition"
            className="form-input"
            type="text"
            value={form.remainsDisposition || ''}
            onChange={e => setField('remainsDisposition', e.target.value)}
            placeholder="Where ashes should go..."
            maxLength={500}
          />
        </div>
      </fieldset>

      <fieldset className="fp-fieldset">
        <legend className="fp-legend">Visitation & Viewing</legend>
        <div className="form-group">
          <label className="form-label" htmlFor="fp-viewingWishes">Viewing Wishes</label>
          <select
            id="fp-viewingWishes"
            className="form-input"
            value={form.viewingWishes || ''}
            onChange={e => setField('viewingWishes', e.target.value)}
          >
            {VIEWING_OPTIONS.map(t => <option key={t} value={t}>{t || '-- Select --'}</option>)}
          </select>
        </div>
        <div className="form-group">
          <label className="form-label" htmlFor="fp-viewingNotes">Viewing Notes</label>
          <textarea
            id="fp-viewingNotes"
            className="form-input"
            value={form.viewingNotes || ''}
            onChange={e => setField('viewingNotes', e.target.value)}
            maxLength={1000}
          />
        </div>
      </fieldset>

      <fieldset className="fp-fieldset">
        <legend className="fp-legend">Clothing & Appearance</legend>
        <div className="form-group">
          <label className="form-label" htmlFor="fp-clothingPreference">Clothing Preference</label>
          <textarea
            id="fp-clothingPreference"
            className="form-input"
            value={form.clothingPreference || ''}
            onChange={e => setField('clothingPreference', e.target.value)}
            placeholder="What to wear..."
            maxLength={1000}
          />
        </div>
        <div className="form-group">
          <label className="form-label" htmlFor="fp-personalItems">Personal Items</label>
          <textarea
            id="fp-personalItems"
            className="form-input"
            value={form.personalItems || ''}
            onChange={e => setField('personalItems', e.target.value)}
            placeholder="e.g., Wedding ring - leave on, Eyeglasses - return to spouse"
            maxLength={1000}
          />
        </div>
      </fieldset>

      <fieldset className="fp-fieldset">
        <legend className="fp-legend">Flowers & Memorial Donations</legend>
        <div className="form-group">
          <label className="form-label" htmlFor="fp-flowerPreference">Flower Preference</label>
          <select
            id="fp-flowerPreference"
            className="form-input"
            value={form.flowerPreference || ''}
            onChange={e => setField('flowerPreference', e.target.value)}
          >
            {FLOWER_OPTIONS.map(t => <option key={t} value={t}>{t || '-- Select --'}</option>)}
          </select>
        </div>
        <div className="form-group">
          <label className="form-label" htmlFor="fp-donationOrganizations">Donation Organizations</label>
          <textarea
            id="fp-donationOrganizations"
            className="form-input"
            value={form.donationOrganizations || ''}
            onChange={e => setField('donationOrganizations', e.target.value)}
            placeholder="Charities for memorial donations..."
            maxLength={1000}
          />
        </div>
      </fieldset>

      <fieldset className="fp-fieldset">
        <legend className="fp-legend">Obituary</legend>
        <div className="form-group">
          <label className="form-label" htmlFor="fp-obituaryWishes">Obituary Wishes</label>
          <textarea
            id="fp-obituaryWishes"
            className="form-input"
            value={form.obituaryWishes || ''}
            onChange={e => setField('obituaryWishes', e.target.value)}
            placeholder="What to include, where to publish..."
            maxLength={2000}
          />
        </div>
      </fieldset>

      <fieldset className="fp-fieldset">
        <legend className="fp-legend">Reception</legend>
        <div className="form-group">
          <label className="form-label" htmlFor="fp-receptionWishes">Reception Wishes</label>
          <textarea
            id="fp-receptionWishes"
            className="form-input"
            value={form.receptionWishes || ''}
            onChange={e => setField('receptionWishes', e.target.value)}
            placeholder="e.g., Church fellowship hall, potluck, family favorites"
            maxLength={2000}
          />
        </div>
      </fieldset>

      <fieldset className="fp-fieldset">
        <legend className="fp-legend">Funeral Home & Pre-Arrangements</legend>
        <div className="form-group">
          <label className="form-label" htmlFor="fp-funeralHome">Funeral Home</label>
          <input
            id="fp-funeralHome"
            className="form-input"
            type="text"
            value={form.funeralHome || ''}
            onChange={e => setField('funeralHome', e.target.value)}
            maxLength={200}
          />
        </div>
        <div className="form-group">
          <label className="form-label" htmlFor="fp-funeralHomePhone">Funeral Home Phone</label>
          <input
            id="fp-funeralHomePhone"
            className="form-input"
            type="tel"
            value={form.funeralHomePhone || ''}
            onChange={e => setField('funeralHomePhone', formatPhoneInput(e.target.value))}
            placeholder="(555) 123-4567"
            maxLength={14}
          />
        </div>
        <div className="form-group">
          <label className="form-label" htmlFor="fp-prePaidArrangements">Pre-Paid Arrangements</label>
          <textarea
            id="fp-prePaidArrangements"
            className="form-input"
            value={form.prePaidArrangements || ''}
            onChange={e => setField('prePaidArrangements', e.target.value)}
            placeholder="Pre-paid plan details, policy numbers..."
            maxLength={1000}
          />
        </div>
      </fieldset>

      <fieldset className="fp-fieldset">
        <legend className="fp-legend">Other</legend>
        <div className="form-group">
          <label className="form-label" htmlFor="fp-veteranInfo">Veteran Information</label>
          <textarea
            id="fp-veteranInfo"
            className="form-input"
            value={form.veteranInfo || ''}
            onChange={e => setField('veteranInfo', e.target.value)}
            placeholder="Branch of service, discharge papers location, honors..."
            maxLength={1000}
          />
        </div>
        <div className="form-group">
          <label className="form-label" htmlFor="fp-organDonation">Organ Donation</label>
          <select
            id="fp-organDonation"
            className="form-input"
            value={form.organDonation || ''}
            onChange={e => setField('organDonation', e.target.value)}
          >
            {ORGAN_OPTIONS.map(t => <option key={t} value={t}>{t || '-- Select --'}</option>)}
          </select>
        </div>
        <div className="form-group">
          <label className="form-label" htmlFor="fp-additionalWishes">Additional Wishes</label>
          <textarea
            id="fp-additionalWishes"
            className="form-input"
            value={form.additionalWishes || ''}
            onChange={e => setField('additionalWishes', e.target.value)}
            placeholder="Anything else, things you do NOT want, etc."
            maxLength={2000}
          />
        </div>
      </fieldset>
    </>
  );
}

// ─── Delete confirmation modal ───

function DeleteModal({ name, deleting, onConfirm, onCancel }: {
  name: string;
  deleting: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <div className="fp-confirm-overlay" onClick={() => !deleting && onCancel()}>
      <div className="fp-confirm-dialog" onClick={e => e.stopPropagation()}>
        <h4>Delete Funeral Plan</h4>
        <p>
          Are you sure you want to delete the plan for <strong>{name}</strong>?
          This action cannot be undone.
        </p>
        <div className="fp-confirm-actions">
          <button className="fp-confirm-delete" onClick={onConfirm} disabled={deleting}>
            {deleting ? 'Deleting...' : 'Delete'}
          </button>
          <button className="fp-confirm-cancel" onClick={onCancel} disabled={deleting}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
